<template>
  <h4 class="mb-2 text-2xs font-bold uppercase text-body-color">
    Inspection-To-Truck-Count
  </h4>
  <div class="flex flex-col space-y-3 rounded-md">
    <router-link
      :to="{
        name: 'Carrier Brokers Show Page',
        params: { id: $store.state.show?.showBroker?.id },
      }"
      target="_blank"
      class="text-xl text-yellow-600 underline"
    >
      {{
        capitalizeWords($store.state.show?.showBroker?.legal_name.toLowerCase())
      }}
    </router-link>
    <div class="grid grid-cols-4 gap-3">
      <div
        class="flex flex-col items-center gap-4 rounded-md border border-yellow-500 p-4 pt-8 text-center"
      >
        <div class="text-4xl text-red-500">
          {{ observedInspections }}
        </div>
        <h4 class="text-2xs font-bold uppercase text-body-color">
          Observed Inspections
        </h4>
      </div>
      <div
        class="flex flex-col items-center gap-4 rounded-md border border-yellow-500 p-4 pt-8 text-center"
      >
        <div class="text-4xl text-body-color">
          {{
            $store.state.carriers?.showCarrierInspections?.reported_power_units
          }}
        </div>
        <h4 class="text-2xs font-bold uppercase text-body-color">
          Reported Power Units In Fleet
        </h4>
      </div>
      <div
        class="flex flex-col items-center gap-3 rounded-md border border-yellow-500 p-4 text-center"
      >
        <div
          class="flex flex-col items-center justify-start text-4xl text-red-500"
        >
          <span
            v-if="showBottomText"
            class="text-sm uppercase"
          >
            Bottom
          </span>
          {{ expectedInspectionsText }}
        </div>
        <h4 class="text-2xs font-bold uppercase text-body-color">
          Percentile Ranking Compared To Peers
        </h4>
      </div>
      <div
        class="flex flex-col items-center gap-4 rounded-md border border-yellow-500 p-4 pt-8 text-center"
      >
        <div
          class="text-4xl"
          :class="authorityColor"
        >
          {{
            $store.state.carriers?.showCarrierInspections
              ?.length_of_authority_in_months == 24
              ? '24+'
              : $store.state.carriers?.showCarrierInspections
                  ?.length_of_authority_in_months
          }}
          <span class="text-xl uppercase">mos</span>
        </div>
        <h4 class="text-2xs font-bold uppercase text-body-color">
          Length Of Authority
        </h4>
      </div>
    </div>
    <div
      class="relative h-80 w-full flex-grow rounded-md border border-yellow-500 p-4 pl-9"
    >
      <div
        class="absolute bottom-0 left-0 top-0 flex h-full w-9 items-center justify-center"
      >
        <div
          class="-rotate-90 transform whitespace-nowrap text-xs uppercase tracking-widest text-gray-over-dark"
        >
          Observed Annual Inspections
        </div>
      </div>
      <div
        class="absolute -bottom-2 left-6 right-0 z-10 flex w-full items-center justify-center rounded-md"
        style="height: 34px"
      >
        <div
          class="whitespace-nowrap text-xs uppercase tracking-widest text-gray-over-dark"
        >
          Reported Power Units
        </div>
      </div>
      <div
        ref="inspectionHistoryTruckCountInsightChartWrap"
        class="h-full w-full"
      >
        <line-chart-insights
          v-if="dataMaximumX > 0 && dataMaximumY > 0"
          :width="chartWidth"
          :height="chartHeight"
          :plot-data="chartData"
          :data-maximum-x="dataMaximumX"
          :data-maximum-y="dataMaximumY"
        ></line-chart-insights>
      </div>
    </div>
  </div>
</template>

<script>
import LineChartInsights from './LineChartInsights.vue';
import fleetInspectionRatios from '@/assets/data/fleetInspectionRatios';

export default {
  name: 'InspectionToTruckCountInsight',
  components: {
    LineChartInsights,
  },
  props: {
    insight: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      chartWidth: 0,
      chartHeight: 0,
      mounted: false,
      chartData: [
        {
          nameB: '0',
          plB: 0,
        },
        {
          name: '0',
          pl: 0,
        },
        {
          nameT: '0',
          plT: 0,
        },
      ],
      top10Pct: 0,
      bottom10Pct: 0,
      limit: 5,
      fleetInspectionRatiosLocal: fleetInspectionRatios,
      dataMaxima: {
        x: 0,
        y: 0,
      },
      dataMaximumX: 0,
      dataMaximumY: 0,
      currentMedian: 0,
      resizeObserver: null,
    };
  },
  computed: {
    observedInspections() {
      return this.$store.state.carriers?.showCarrierInspections?.records.length;
    },
    authorityColor() {
      var authority =
        this.$store.state.carriers?.showCarrierInspections
          ?.length_of_authority_in_months;
      if (authority > 15) {
        return 'text-green-500';
      } else if (authority > 6) {
        return 'text-orange-500';
      } else {
        return 'text-red-500';
      }
    },
    showBottomText() {
      var text =
        this.$store.state.carriers?.showCarrierInspections
          ?.percentile_ranking_compared_to_peers;
      if (text && text.includes('Bottom')) {
        return true;
      }
      return false;
    },
    expectedInspectionsText() {
      var text =
        this.$store.state.carriers?.showCarrierInspections
          ?.percentile_ranking_compared_to_peers;
      if (text && text.includes('Bottom ')) {
        return text.replace('Bottom ', '');
      }
      return text;
    },
    powerUnits() {
      return (
        this.$store.state.carriers?.showCarrierInspections
          ?.reported_power_units || ''
      );
    },
    expectedYVal() {
      return this.calculateYValue(this.powerUnits, 'median');
    },
  },
  mounted() {
    this.$nextTick(function () {
      this.mounted = true;
    });
    this.resizeObserver = new ResizeObserver((entries) => {
      // We wrap it in requestAnimationFrame to avoid "ResizeObserver loop limit exceeded" error
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        this.resizeChart();
      });
    });
    this.resizeObserver.observe(
      this.$refs.inspectionHistoryTruckCountInsightChartWrap
    );
    // window.addEventListener("resize", this.resizeChart);
    this.resizeChart();
    this.initChartData();
    console.dir(this.chartData);
  },
  beforeUnmount() {
    this.resizeObserver.unobserve(
      this.$refs.inspectionHistoryTruckCountInsightChartWrap
    );
    // window.removeEventListener('resize', this.resizeChart);
  },
  methods: {
    capitalizeWords: function (str) {
      return str
        .toLowerCase()
        .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
        .replace('Llc', 'LLC');
    },
    resizeChart: function () {
      this.chartWidth =
        this.$refs.inspectionHistoryTruckCountInsightChartWrap?.clientWidth;
      this.chartHeight =
        this.$refs.inspectionHistoryTruckCountInsightChartWrap?.clientHeight;
    },
    initChartData: function () {
      var self = this;

      this.chartData.push({
        name2: this.powerUnits.toString(),
        pl2: this.observedInspections,
        msg: 'Observed',
      });

      var topIndexShown = 0;
      var dataBVal = 0;
      var dataTVal = 0;
      this.fleetInspectionRatiosLocal.forEach(function (item, index) {
        var nameVal = (item.fleet_size_min + (item.fleet_size_max - 1)) / 2;
        var plVal = self.calculateYValue(nameVal, 'median');
        var dataMax = self.powerUnits * 2;
        if (dataMax > item.fleet_size_min) {
          self.chartData.push({
            name: nameVal,
            pl: plVal,
          });
        } else {
          if (topIndexShown == 0) {
            topIndexShown = index;
            self.chartData.push({
              name: nameVal,
              pl: plVal,
            });
          }
        }

        if (topIndexShown == 0 || index < topIndexShown + 1) {
          var plTVal = self.calculateYValue(nameVal, 'top_25_percent');
          self.chartData.push({
            nameT: nameVal,
            plT: plTVal,
          });

          var plBVal = self.calculateYValue(nameVal, 'bottom_25_percent');
          self.chartData.push({
            nameB: nameVal,
            plB: plBVal,
          });
        }

        dataBVal = self.powerUnits * 1.5;
        if (
          (dataBVal == item.fleet_size_min ||
            dataBVal > item.fleet_size_min - 1) &&
          (dataBVal == item.fleet_size_max || dataBVal < item.fleet_size_max)
        ) {
          self.chartData.push({
            nameBLabel: nameVal,
            plBLabel: plBVal,
            msgBLabel: 'Bottom 25%',
          });
        }

        dataTVal = self.powerUnits / 3;
        if (
          (dataTVal == item.fleet_size_min ||
            dataTVal > item.fleet_size_min - 1) &&
          (dataTVal == item.fleet_size_max || dataTVal < item.fleet_size_max)
        ) {
          self.chartData.push({
            nameTLabel: nameVal,
            plTLabel: plTVal,
            msgTLabel: 'Top 25%',
          });
        }
      });

      var topItem = this.fleetInspectionRatiosLocal[topIndexShown];
      var nameTopVal = (topItem.fleet_size_min + topItem.fleet_size_max) / 2;
      var plTopVal = topItem.fleet_size_max * topItem.top_25_percent;

      this.chartData.push({
        nameTop: '0',
        plTop: plTopVal,
      });
      this.chartData.push({
        nameTop: nameTopVal,
        plTop: plTopVal,
      });

      this.dataMaximumX = this.powerUnits * 2;
      this.dataMaximumY =
        (this.calculateYValue(this.dataMaximumX, 'median') +
          this.calculateYValue(this.dataMaximumX, 'top_25_percent')) /
        2;

      this.chartData.push({
        name3:
          this.$store.state.carriers?.showCarrierInspections?.reported_power_units.toString(),
        pl3: this.expectedYVal,
        msg: 'Expected',
      });
    },
    calculateYValue: function (value, percentile = 'median') {
      var self = this;
      var yVal = 0;
      var base = 0;
      var baseProp = percentile + '_base';
      // switch (percentile) {
      //   case 'median':
      //     base = 'median_base';
      //     break;
      //   case 'bottom_25_percentile':
      //     base = 'bottom_25_base';
      //     break;
      //   case 'top_25_percentile':
      //     break;
      // }
      this.fleetInspectionRatiosLocal.forEach(function (item) {
        base = parseFloat(item[baseProp]);
        if (
          (value == item.fleet_size_min || value > item.fleet_size_min) &&
          (value == item.fleet_size_max || value < item.fleet_size_max)
        ) {
          if (item.fleet_size_min !== 0) {
            yVal =
              base + (value - (item.fleet_size_min - 1)) * item[percentile];
          } else {
            yVal = base + (value - item.fleet_size_min) * item[percentile];
          }

          self.currentMedian = item.median;
        }
      });
      // console.log('calculateYValue returning 0...');
      return yVal;
    },
    calculateExpected: function (value) {
      // var self = this;
      var yVal = 0;
      // var yValOther = 0;
      var base = 0;
      var percentile = 'median';
      var baseProp = percentile + '_base';
      // switch (percentile) {
      //   case 'median':
      //     base = 'median_base';
      //     break;
      //   case 'bottom_25_percentile':
      //     base = 'bottom_25_base';
      //     break;
      //   case 'top_25_percentile':
      //     break;
      // }
      this.fleetInspectionRatiosLocal.forEach(function (item) {
        base = parseFloat(item[baseProp]);
        if (
          (value == item.fleet_size_min || value > item.fleet_size_min) &&
          (value == item.fleet_size_max || value < item.fleet_size_max)
        ) {
          if (item.fleet_size_min !== 0) {
            yVal =
              base + (value - (item.fleet_size_min - 1)) * item[percentile];
          } else {
            yVal = base + (value - item.fleet_size_min) * item[percentile];
          }
        } else {
          // var x = (item.fleet_size_max + (item.fleet_size_min - 1)) / 2;
          // yValOther = base + ((x - (item.fleet_size_min - 1))/2) * item[percentile];
        }
      });
      // console.log('calculateYValue returning 0...');
      return yVal;
    },
  },
  watch: {
    powerUnits: function () {
      this.initChartData();
    },
  },
};
</script>
